import { useFetchWrapper } from '_helpers';

export { usePushWrapper };

/** VAPID public keys are base64url; the PushManager wants a Uint8Array. */
function urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const raw = window.atob(base64);
    const output = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; ++i) output[i] = raw.charCodeAt(i);
    return output;
}

/** True only where the full Web Push stack is available (and, on iOS, when installed). */
function isPushSupported() {
    return (
        typeof window !== 'undefined' &&
        'serviceWorker' in navigator &&
        'PushManager' in window &&
        'Notification' in window
    );
}

function usePushWrapper() {
    const fetchWrapper = useFetchWrapper();

    /** Current state for UI: 'unsupported' | 'denied' | 'subscribed' | 'default'. */
    async function getStatus() {
        if (!isPushSupported()) return 'unsupported';
        if (Notification.permission === 'denied') return 'denied';
        const reg = await navigator.serviceWorker.getRegistration();
        const sub = reg && (await reg.pushManager.getSubscription());
        return sub ? 'subscribed' : 'default';
    }

    /** Ask permission, subscribe, and persist the subscription server-side. */
    async function enablePush() {
        if (!isPushSupported()) {
            return { ok: false, error: 'Trình duyệt không hỗ trợ thông báo' };
        }
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
            return { ok: false, error: 'Bạn chưa cho phép gửi thông báo' };
        }

        const keyRes = await fetchWrapper.get('/api/push/vapid-public-key', null, null);
        const keyJson = await keyRes.json();
        if (keyJson.status !== 'Success') {
            return { ok: false, error: keyJson.message || 'Không lấy được khóa máy chủ' };
        }

        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(keyJson.message.publicKey),
        });

        const res = await fetchWrapper.post(
            '/api/push/subscribe',
            'application/json',
            JSON.stringify({ subscription: sub })
        );
        const json = await res.json();
        return json.status === 'Success'
            ? { ok: true }
            : { ok: false, error: json.message };
    }

    /** Unsubscribe locally and tell the server to drop the row. */
    async function disablePush() {
        if (!isPushSupported()) return { ok: true };
        const reg = await navigator.serviceWorker.getRegistration();
        const sub = reg && (await reg.pushManager.getSubscription());
        if (sub) {
            try {
                await fetchWrapper.post(
                    '/api/push/unsubscribe',
                    'application/json',
                    JSON.stringify({ endpoint: sub.endpoint })
                );
            } catch (_) { /* drop locally regardless */ }
            await sub.unsubscribe().catch(() => {});
        }
        return { ok: true };
    }

    return { isPushSupported, getStatus, enablePush, disablePush };
}
